import { createFileRoute } from "@tanstack/react-router";
import { Button } from "~/components/Button";
import { loadSecurityPage } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";

export const Route = createFileRoute("/security")({
  loader: async () => {
    const { index, blocks, renderedBlocks } = await loadSecurityPage();

    // Reconstruct blocks array from the block references in index
    const blockPaths = (index?.blocks as string[]) || [];
    const resolvedBlocks = blockPaths.map((blockPath: string) => {
      // Find matching block frontmatter
      for (const [key, data] of Object.entries(blocks || {})) {
        if (key.includes(blockPath.replace("security/blocks/", ""))) {
          const html = renderedBlocks[key] || "";
          return { ...(data as Record<string, any>), html, _path: blockPath };
        }
      }
      return { _path: blockPath, layout: "unknown", html: "" };
    });

    return {
      title: (index?.title as string) || "Security",
      seo_title: (index?.seo_title as string) || "Security - C1",
      seo_description: index?.seo_description as string | undefined,
      blocks: resolvedBlocks,
    };
  },
  head: ({ loaderData }) => ({
    meta: loaderData ? seo({ title: loaderData.seo_title || loaderData.title, description: loaderData.seo_description, url: "/security" }) : [],
    links: seoLinks({ url: "/security" }),
  }),
  component: Security,
});

/* ------------------------------------------------------------------ */
/*  Inline icon set                                                    */
/*                                                                     */
/*  Authored here rather than pulled from a CDN icon script: the        */
/*  design mock loads lucide from unpkg, which would add a blocking     */
/*  third-party request and a runtime dependency to a page that needs   */
/*  neither. Same 24px stroke geometry, no network cost.                */
/* ------------------------------------------------------------------ */
const ICON_PATHS: Record<string, React.ReactNode> = {
  mail: (
    <>
      <rect x="2.5" y="4.5" width="19" height="15" rx="2" />
      <path d="M3 7l9 5.5L21 7" />
    </>
  ),
  bug: (
    <>
      <rect x="8" y="6" width="8" height="12" rx="4" />
      <path d="M8 10H4M8 14H4M16 10h4M16 14h4M9.5 6.2 8.5 4M14.5 6.2l1-2.2" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.6 2.6 15.4 0 18M12 3c-2.6 2.6-2.6 15.4 0 18" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M16 5.5a3.5 3.5 0 0 1 0 7M18 14.6c1.8.9 3 2.6 3 5.4" />
    </>
  ),
  network: (
    <>
      <rect x="9" y="3" width="6" height="5" rx="1" />
      <rect x="2" y="16" width="6" height="5" rx="1" />
      <rect x="16" y="16" width="6" height="5" rx="1" />
      <path d="M12 8v3.5M5 16v-2.5h14V16" />
    </>
  ),
  database: (
    <>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </>
  ),
  activity: <path d="M3 12h4l3-8 4 16 3-8h4" />,
  check: <path d="M4.5 12.5l5 5L20 6.5" />,
  "clipboard-list": (
    <>
      <rect x="5" y="5" width="14" height="16" rx="2" />
      <path d="M9 5V3.5h6V5" />
      <path d="M9 11h6M9 15h6" />
    </>
  ),
  "circle-slash": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 18.4 18.4 5.6" />
    </>
  ),
  "shield-check": (
    <>
      <path d="M12 3l8 3.5V12c0 5-3.5 9.5-8 11-4.5-1.5-8-6-8-11V6.5L12 3z" />
      <path d="M8.5 12l2.5 2.5L15.5 10" />
    </>
  ),
  "arrow-up-right": <path d="M7 17 17 7M9 7h8v8" />,
};

function Icon({ name, className }: { name?: string; className?: string }) {
  const paths = name ? ICON_PATHS[name] : null;
  if (!paths) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}

/**
 * Icon on a pink rounded square — the treatment this page carried before the v2
 * redesign (36px, rx=8, pink-accent behind a teal glyph). Reserved for display
 * icons: the stat tiles, the domain jump cards and the disclosure card headers.
 * Deliberately not used for the inline checkmarks or for icons inside buttons.
 */
function IconBadge({ name, small = false }: { name?: string; small?: boolean }) {
  if (!name || !ICON_PATHS[name]) return null;
  return (
    <span
      className={`inline-flex flex-none items-center justify-center bg-pink-accent ${
        small ? "size-7 rounded-md" : "size-9 rounded-lg"
      }`}
    >
      <Icon name={name} className={`${small ? "size-4" : "size-5"} text-teal-dark`} />
    </span>
  );
}

/** Small uppercase section label above a heading. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-body text-13 font-semibold uppercase tracking-[0.08em] text-teal-dark">
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Security page component                                            */
/* ------------------------------------------------------------------ */
function Security() {
  const { blocks } = Route.useLoaderData();

  return (
    <div className="bg-white">
      {(blocks as any[]).map((block: any, i: number) => {
        const layout = block.layout || "default";

        /* ---- Hero: two columns over a stat strip ---- */
        if (layout === "hero") {
          const stats = (block.stats || []) as any[];
          return (
            <section key={i} className="px-4 pt-12 md:px-8 lg:pt-20">
              <div className="mx-auto grid max-w-screen-xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
                <div>
                  {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
                  <h1 className="mt-5 font-display text-40 font-bold text-charcoal md:text-48 xl:text-57">
                    {block.heading}
                  </h1>
                  {block.lead && (
                    <p className="mt-6 max-w-[44ch] font-body leading-relaxed text-gray-80 md:text-19">
                      {block.lead}
                    </p>
                  )}
                  {(block.primary_cta_href || block.secondary_cta_href) && (
                    <div className="mt-8 flex flex-wrap gap-3">
                      {block.primary_cta_href && (
                        <Button
                          href={block.primary_cta_href}
                          variant="primary"
                          size="md"
                          className="gap-2"
                        >
                          <Icon name={block.primary_cta_icon} className="size-4" />
                          {block.primary_cta_text}
                        </Button>
                      )}
                      {block.secondary_cta_href && (
                        <Button
                          href={block.secondary_cta_href}
                          variant="secondary"
                          size="md"
                          className="gap-2"
                        >
                          <Icon name={block.secondary_cta_icon} className="size-4" />
                          {block.secondary_cta_text}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-10 bg-teal-dark">
                  <Icon
                    name="lock"
                    className="absolute inset-0 m-auto size-[88%] text-white opacity-[0.07]"
                  />
                  <svg
                    viewBox="0 0 118 112"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    /* Centred on the lock's body, not the tile: the body rect spans
                       y=10..20 of the icon's 24-unit box, so its middle is 62.5% of
                       an icon that is itself 88% of the tile and vertically centred
                       — 6% + (0.625 x 88%) = 61% down the tile. */
                    className="absolute left-1/2 top-[61%] w-[15%] -translate-x-1/2 -translate-y-1/2 text-pink-accent"
                    aria-hidden="true"
                  >
                    <path
                      d="M39.475 10.9122H9.23382C4.13756 10.9122 0 15.0441 0 20.1334V70.7407C0 74.7214 2.13606 78.383 5.58402 80.3481L59.5237 111.085C62.938 113.034 67.2942 111.959 69.4135 108.65L72.0541 104.535C73.4165 102.418 75.7543 101.125 78.2773 101.125H110.352C114.439 101.125 117.735 97.8163 117.735 93.7516V41.3303C117.735 37.3496 115.599 33.688 112.151 31.7228L58.2118 0.985571C54.7974 -0.962801 50.4412 0.112163 48.322 3.42104L45.6982 7.51934C44.3358 9.63567 41.9979 10.929 39.475 10.929V10.9122ZM30.8635 53.7428V28.9178C30.8635 26.0625 33.9751 24.2821 36.4475 25.7433L83.3399 53.4405C85.5937 54.7674 86.9728 57.186 86.9728 59.7895V84.6144C86.9728 87.4698 83.8613 89.2502 81.3888 87.7889L34.5133 60.0918C32.2595 58.7649 30.8803 56.3462 30.8803 53.7428H30.8635Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
              </div>
              {stats.length > 0 && (
                /* Column count follows the number of stats — a fixed 3-up grid
                   leaves an empty cell rendering as a bare grey block, because
                   the divider lines are the container's background showing
                   through the 1px gaps. */
                <div
                  className={`mx-auto mt-12 grid max-w-screen-xl gap-px overflow-hidden rounded-10 border border-gray-20 bg-gray-20 sm:grid-cols-2 ${
                    stats.length >= 3 ? "lg:grid-cols-3" : ""
                  }`}
                >
                  {stats.map((stat: any, si: number) => (
                    <div key={si} className="bg-white p-6">
                      <IconBadge name={stat.icon} />
                      <div className="mt-4 font-display text-28 font-bold text-charcoal md:text-33">
                        {stat.value}
                      </div>
                      <div className="mt-1.5 font-body text-13 leading-relaxed text-gray-60">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        }

        /* ---- Our approach: heading beside prose + SOC 2 card ---- */
        if (layout === "approach") {
          const paragraphs = (block.paragraphs || []) as string[];
          return (
            <section key={i} className="px-4 py-16 md:px-8 lg:py-24">
              <div className="mx-auto grid max-w-screen-xl items-start gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
                <div>
                  {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
                  <h2 className="mt-4 font-display text-33 font-bold leading-tight text-charcoal md:text-40">
                    {block.heading}
                  </h2>
                </div>
                <div className="flex flex-col gap-5">
                  {paragraphs.map((paragraph: string, pi: number) => (
                    <p key={pi} className="font-body leading-relaxed text-gray-80 md:text-19">
                      {paragraph}
                    </p>
                  ))}
                  {block.soc_link_href && (
                    /* The bordered card only earns its chrome when there is copy to
                       frame. With just the button left, a box around it reads as an
                       empty container — so the wrapper drops back to bare spacing.
                       Re-adding soc_heading/soc_copy restores the card. */
                    <div
                      className={
                        block.soc_heading || block.soc_copy
                          ? "mt-2 flex flex-col items-start gap-4 rounded-lg border border-gray-20 p-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
                          : "mt-2"
                      }
                    >
                      {(block.soc_heading || block.soc_copy) && (
                        <div>
                          {block.soc_heading && (
                            <div className="font-body font-semibold text-charcoal">
                              {block.soc_heading}
                            </div>
                          )}
                          {block.soc_copy && (
                            <p className="mt-1 font-body text-13 leading-relaxed text-gray-80">
                              {block.soc_copy}
                            </p>
                          )}
                        </div>
                      )}
                      <Button
                        href={block.soc_link_href}
                        variant="primary"
                        size="md"
                        className="flex-none gap-2"
                      >
                        <Icon name="shield-check" className="size-4" />
                        {block.soc_link_text}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        }

        /* ---- Control domains: centred intro + jump cards ---- */
        if (layout === "domains") {
          const cards = (block.cards || []) as any[];
          return (
            <section
              key={i}
              className="border-y border-gray-20 bg-cream px-4 py-16 md:px-8 lg:py-24"
            >
              <div className="mx-auto max-w-screen-xl">
                <div className="mx-auto max-w-2xl text-center">
                  {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
                  <h2 className="mt-4 font-display text-33 font-bold leading-tight text-charcoal md:text-40">
                    {block.heading}
                  </h2>
                  {block.intro && (
                    <p className="mt-4 font-body leading-relaxed text-gray-80 md:text-19">
                      {block.intro}
                    </p>
                  )}
                </div>
                <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                  {cards.map((card: any, ci: number) => (
                    <a
                      key={ci}
                      href={card.href}
                      className="flex flex-col gap-3 rounded-lg border border-gray-20 bg-white p-6 transition-colors hover:border-teal-dark"
                    >
                      <IconBadge name={card.icon} />
                      <div className="font-body font-semibold text-charcoal">{card.heading}</div>
                      <div className="font-body text-sm leading-relaxed text-gray-60">
                        {card.copy}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </section>
          );
        }

        /* ---- Numbered control domain: sticky label + checked claims ---- */
        if (layout === "domain") {
          const items = (block.items || []) as string[];
          return (
            <section key={i} className="px-4 pt-16 md:px-8 lg:pt-24">
              {/* The anchor sits on the content, not the <section> — targeting the
                  section box lands the jump above its own top padding, leaving the
                  previous section's last rows filling the top of the viewport. */}
              <div
                id={block.anchor}
                className="mx-auto grid max-w-screen-xl scroll-mt-24 items-start gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16"
              >
                <div className="lg:sticky lg:top-24">
                  {block.number && <Eyebrow>{block.number}</Eyebrow>}
                  <h2 className="mt-4 font-display text-28 font-bold leading-tight text-charcoal md:text-33 lg:text-40">
                    {block.heading}
                  </h2>
                  {block.lead && (
                    <p className="mt-4 font-body leading-relaxed text-gray-80">{block.lead}</p>
                  )}
                </div>
                <ul className="flex flex-col">
                  {items.map((item: string, ii: number) => (
                    <li
                      key={ii}
                      className="flex items-start gap-3.5 border-b border-gray-30 py-5 first:pt-0 last:border-b-0 last:pb-0"
                    >
                      <Icon name="check" className="mt-1 size-4.5 flex-none text-teal-dark" />
                      <span className="font-body leading-relaxed text-charcoal">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          );
        }

        /* ---- Architecture diagram ---- */
        if (layout === "diagram") {
          return (
            <section
              key={i}
              className="mt-16 border-y border-gray-20 bg-cream px-4 py-16 md:px-8 lg:mt-24 lg:py-24"
            >
              <div id="architecture" className="mx-auto max-w-screen-xl scroll-mt-24">
                <div className="mx-auto max-w-2xl text-center">
                  {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
                  <h2 className="mt-4 font-display text-33 font-bold leading-tight text-charcoal md:text-40">
                    {block.heading}
                  </h2>
                  {block.intro && (
                    <p className="mt-4 font-body leading-relaxed text-gray-80 md:text-19">
                      {block.intro}
                    </p>
                  )}
                </div>
                {block.image && (
                  <div className="mt-10 rounded-10 border border-gray-20 bg-white p-5 md:p-8">
                    <picture>
                      {block.image_mobile && (
                        <source media="(max-width: 767px)" srcSet={block.image_mobile} />
                      )}
                      <img
                        src={block.image}
                        alt={block.heading || "High level architecture"}
                        className="w-full"
                        loading="lazy"
                      />
                    </picture>
                  </div>
                )}
              </div>
            </section>
          );
        }

        /* ---- Vulnerability reporting ---- */
        if (layout === "vulnerability-reporting") {
          const include = (block.include || []) as string[];
          const outOfScope = (block.out_of_scope || []) as string[];
          const listCards = [
            { heading: block.include_heading, icon: "clipboard-list", items: include },
            { heading: block.out_of_scope_heading, icon: "circle-slash", items: outOfScope },
          ].filter((card) => card.heading && card.items.length > 0);

          return (
            <section key={i} className="px-4 py-16 md:px-8 lg:py-24">
              <div id="vulnerabilities" className="mx-auto max-w-screen-xl scroll-mt-24">
                <div className="mx-auto max-w-3xl text-center">
                  {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
                  <h2 className="mt-4 font-display text-33 font-bold leading-tight text-charcoal md:text-40">
                    {block.heading}
                  </h2>
                  {block.intro && (
                    <p className="mt-4 font-body leading-relaxed text-gray-80 md:text-19">
                      {block.intro}
                    </p>
                  )}
                </div>

                {block.callout && (
                  <div className="mx-auto mt-10 flex max-w-4xl items-start gap-3.5 rounded-lg border border-teal-dark/20 bg-teal-light p-6">
                    <Icon name="mail" className="mt-1 size-4.5 flex-none text-teal-dark" />
                    <div className="font-body leading-relaxed text-teal-dark">
                      <div dangerouslySetInnerHTML={{ __html: block.callout }} />
                      {block.disclosure_request && (
                        <p className="mt-3">{block.disclosure_request}</p>
                      )}
                    </div>
                  </div>
                )}

                {listCards.length > 0 && (
                  <div className="mx-auto mt-5 grid max-w-4xl gap-5 md:grid-cols-2">
                    {listCards.map((card, ci: number) => (
                      <div
                        key={ci}
                        className="rounded-lg border border-gray-20 bg-white p-6"
                      >
                        <div className="flex items-center gap-2.5">
                          <IconBadge name={card.icon} small />
                          <div className="font-body font-semibold text-charcoal">
                            {card.heading}
                          </div>
                        </div>
                        <ul className="mt-5 flex flex-col gap-2.5">
                          {card.items.map((item: string, li: number) => (
                            <li
                              key={li}
                              className="flex gap-2.5 font-body text-sm leading-relaxed text-gray-80"
                            >
                              <span className="text-gray-60">&mdash;</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          );
        }

        /* ---- Closing contact band ---- */
        if (layout === "cta") {
          return (
            <section key={i} className="bg-teal-dark px-4 py-20 md:px-8 lg:py-28">
              <div
                id="contact"
                className="mx-auto flex max-w-screen-xl scroll-mt-24 flex-col items-center gap-6 text-center"
              >
                {block.mark && <img src={block.mark} alt="" className="w-16" loading="lazy" />}
                <h2 className="font-display text-40 font-bold leading-tight text-white md:text-48">
                  {block.heading}
                </h2>
                {block.copy && (
                  <div
                    className="max-w-[52ch] font-body leading-relaxed text-white/70 md:text-19"
                    dangerouslySetInnerHTML={{ __html: block.copy }}
                  />
                )}
                {(block.primary_cta_href || block.secondary_cta_href) && (
                  <div className="mt-2 flex flex-wrap justify-center gap-3">
                    {block.primary_cta_href && (
                      <Button
                        href={block.primary_cta_href}
                        variant="white"
                        size="md"
                        className="gap-2"
                      >
                        <Icon name={block.primary_cta_icon} className="size-4" />
                        {block.primary_cta_text}
                      </Button>
                    )}
                    {block.secondary_cta_href && (
                      <Button
                        href={block.secondary_cta_href}
                        variant="white-outline"
                        size="md"
                        className="gap-2"
                      >
                        <Icon name={block.secondary_cta_icon} className="size-4" />
                        {block.secondary_cta_text}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </section>
          );
        }

        /* ---- Default fallback ---- */
        return (
          <div key={i} className="px-4 py-12 md:px-8">
            <div className="mx-auto max-w-screen-xl">
              {block.heading && (
                <h2 className="mb-4 font-display text-28 font-bold text-charcoal md:text-33 lg:text-40">
                  {block.heading}
                </h2>
              )}
              {block.html && <div className="content" dangerouslySetInnerHTML={{ __html: block.html }} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}
